import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Login() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const error = params.get("error");

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-obsidian text-white">
      {/* Left: brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-border relative overflow-hidden">
        <div className="absolute inset-0 sr-stripe opacity-30 pointer-events-none" />
        <Logo size={40} />
        <div className="max-w-md relative">
          <div className="sr-pill text-teal mb-5"><span className="h-1.5 w-1.5 bg-teal rounded-full sr-blink" /> S.A.I.</div>
          <h2 className="font-display text-3xl tracking-tight leading-tight">
            Calm, grounded, solution-focused communications — at the speed of paste.
          </h2>
          <p className="mt-4 text-sm text-white/55 leading-relaxed">
            SaiResponse is engineered for support directors, account executives, and ops leaders running high-stakes conversations every week.
          </p>
        </div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/30 relative">
          Smart Automated Intelligence
        </div>
      </div>

      {/* Right: auth */}
      <div className="flex flex-col">
        <div className="px-6 lg:px-10 py-5 border-b border-border">
          <button
            data-testid="back-to-landing"
            onClick={() => nav("/")}
            className="text-sm text-white/50 hover:text-white inline-flex items-center gap-1"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
        </div>
        <div className="flex-1 grid place-items-center px-6 lg:px-10">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8"><Logo size={36} /></div>
            <h1 className="font-display text-3xl text-white tracking-tight">Sign in</h1>
            <p className="mt-2 text-sm text-white/55">3-day trial activates instantly. No card required.</p>

            <Button
              data-testid="google-signin-btn"
              onClick={handleGoogle}
              className="mt-8 w-full h-12 bg-white text-obsidian hover:bg-white/90 rounded-sm font-medium"
            >
              <GoogleIcon /> Continue with Google
            </Button>

            {error && (
              <div data-testid="login-error" className="mt-4 text-sm text-red-critical bg-red-critical/10 border border-red-critical/30 rounded-sm px-3 py-2">
                Authentication failed. Please try again.
              </div>
            )}

            <div className="mt-8 flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em] text-white/40">
              <ShieldCheck className="h-3 w-3 text-teal" /> Disposable email domains blocked at signup
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" className="mr-2">
    <path fill="#EA4335" d="M12 11v3.6h5.1c-.2 1.2-1.5 3.5-5.1 3.5-3.1 0-5.6-2.5-5.6-5.6S8.9 6.9 12 6.9c1.8 0 2.9.7 3.6 1.4l2.4-2.4C16.5 4.4 14.5 3.5 12 3.5 7.3 3.5 3.5 7.3 3.5 12s3.8 8.5 8.5 8.5c4.9 0 8.2-3.4 8.2-8.3 0-.6-.1-1-.1-1.5H12z"/>
  </svg>
);
